// Iter47r — Big blocking notification for founders when signups are waiting.
// Auto-opens on app-load + can be reopened from the Équipe badge or the bell button.
import { useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Check, X, UserPlus, Mail, Clock } from './icons';

const roleLabel = (r) => (r === 'founder' ? 'Founder' : r === 'client' ? 'Client' : 'Monteur');

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  } catch { return ''; }
}

export function PendingSignupsModal({ pending, onClose, onDone }) {
  const [busy, setBusy] = useState({});

  const act = async (uid, action) => {
    setBusy((b) => ({ ...b, [uid]: action }));
    try {
      await api.post(`/users/${uid}/${action}`);
      toast.success(action === 'approve' ? 'Compte approuvé' : 'Demande refusée');
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[uid]; return n; });
    }
  };

  if (!pending || pending.length === 0) return null;

  return (
    <div
      data-testid="pending-signups-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(6, 16, 32, 0.72)',
        backdropFilter: 'blur(6px)',
        display: 'grid', placeItems: 'center', padding: 20,
        animation: 'fadeIn 0.15s var(--ease-out)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--sf)',
          border: '1.5px solid rgba(91,190,252,0.35)',
          borderRadius: 16,
          width: '100%', maxWidth: 520,
          boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
          overflow: 'hidden',
          animation: 'slideUp 0.22s var(--ease-out)',
        }}
      >
        {/* Header banner */}
        <div style={{
          padding: '20px 22px',
          background: 'linear-gradient(135deg, rgba(91,190,252,0.18), rgba(46,163,242,0.10))',
          borderBottom: '1px solid var(--bd)',
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: 'rgba(91,190,252,0.20)',
            border: '1.5px solid rgba(91,190,252,0.45)',
            display: 'grid', placeItems: 'center',
            animation: 'pulse 2s ease-in-out infinite',
          }}>
            <UserPlus size={22} color="#5BBEFC" strokeWidth={2} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 17, color: 'var(--t1)', letterSpacing: '-0.02em' }}>
              {pending.length === 1 ? 'Nouvelle demande de connexion' : `${pending.length} demandes en attente`}
            </div>
            <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>
              Approuve ou refuse pour les faire entrer dans ASA
            </div>
          </div>
          <button
            data-testid="pending-modal-close"
            onClick={onClose}
            title="Plus tard"
            style={{
              width: 30, height: 30, borderRadius: 8,
              background: 'transparent', border: '1px solid var(--bd)',
              color: 'var(--t3)', display: 'grid', placeItems: 'center',
              cursor: 'pointer', flexShrink: 0,
            }}
          ><X size={13} /></button>
        </div>

        {/* Pending list */}
        <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {pending.map((p) => (
              <div
                key={p.id}
                data-testid={`pending-modal-user-${p.id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 14px', borderRadius: 12,
                  background: 'var(--sf2)',
                  border: '1px solid var(--bd)',
                }}
              >
                <div style={{
                  width: 42, height: 42, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #5BBEFC, #2EA3F2)',
                  color: '#fff', display: 'grid', placeItems: 'center',
                  fontFamily: 'Sora', fontWeight: 800, fontSize: 15,
                  flexShrink: 0,
                }}>
                  {((p.first_name?.[0] || '') + (p.last_name?.[0] || '')).toUpperCase() || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 14,
                    color: 'var(--t1)', letterSpacing: '-0.005em',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{p.first_name} {p.last_name}</div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)',
                    marginTop: 2,
                    overflow: 'hidden',
                  }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <Mail size={10} /> {p.email}
                    </span>
                    <span>·</span>
                    <span style={{
                      padding: '1px 6px', borderRadius: 999,
                      background: 'rgba(91,190,252,0.14)', color: '#5BBEFC',
                      fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5,
                      letterSpacing: '0.04em', textTransform: 'uppercase',
                    }}>{roleLabel(p.role)}</span>
                    {p.created_at && (
                      <>
                        <span>·</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          <Clock size={9} /> {formatWhen(p.created_at)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  data-testid={`pending-modal-approve-${p.id}`}
                  onClick={() => act(p.id, 'approve')}
                  disabled={!!busy[p.id]}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    height: 32, padding: '0 12px', borderRadius: 8,
                    background: 'rgba(52,211,153,0.16)', border: '1.5px solid rgba(52,211,153,0.45)',
                    color: '#34D399', fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                    cursor: busy[p.id] ? 'progress' : 'pointer', flexShrink: 0,
                    opacity: busy[p.id] === 'reject' ? 0.4 : 1,
                  }}
                ><Check size={12} /> Approuver</button>
                <button
                  data-testid={`pending-modal-reject-${p.id}`}
                  onClick={() => act(p.id, 'reject')}
                  disabled={!!busy[p.id]}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    height: 32, padding: '0 10px', borderRadius: 8,
                    background: 'rgba(255,107,107,0.12)', border: '1.5px solid rgba(255,107,107,0.40)',
                    color: '#FF6B6B', fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                    cursor: busy[p.id] ? 'progress' : 'pointer', flexShrink: 0,
                    opacity: busy[p.id] === 'approve' ? 0.4 : 1,
                  }}
                ><X size={12} /> Refuser</button>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 22px',
          borderTop: '1px solid var(--bd)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'var(--sf2)',
        }}>
          <span style={{ fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)' }}>
            Les décisions sont immédiates et irréversibles
          </span>
          <button
            data-testid="pending-modal-later"
            onClick={onClose}
            style={{
              padding: '7px 14px', borderRadius: 8,
              background: 'transparent', border: '1px solid var(--bd)',
              color: 'var(--t2)', fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
              cursor: 'pointer',
            }}
          >Plus tard</button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(91,190,252,0.4); }
          50% { box-shadow: 0 0 0 8px rgba(91,190,252,0); }
        }
      `}</style>
    </div>
  );
}
